import {PreferenceFacturationEnum} from '../../../preferences-avis-de-pub/model/preference-facturation';
import {Preference} from '../../../preferences-avis-de-pub/model/preference';
import {LogService} from '../../../../shared/services/log.service';
import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Step9} from '../../../../shared/models/component-messages/blocCommun/mapa/step9';
import {TypeFacturation} from '../../../../../models/eforms/dila/TypeFacturation';
import {FacturationMessage} from '../../../../shared/models/component-messages/facturation/facturation';
import {FacturationService} from '../../../../formulaires/common/services/facturation/facturation.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {TypeAdresseTEMPOSTComponent} from '../typeAdresseTEMPOST/type-adresseTEMPOST.component';
import {TypeEntite, TypeEntiteChangeEvent} from '../../../../../models/typeEntite';

@Component({
  selector: 'app-type-facturation',
  templateUrl: './type-facturation.component.html'
})
export class TypeFacturationComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(private facturationService: FacturationService) {
  }

  // Ordre de traitement requis dans chargeDonneePreference :
  // - CLASSE_PROFIT en premier : sa pose déclenche applySiretState(), qui vide le SIRET si la classe
  //   n'est pas "etat" — traitée après, elle effacerait le SIRET tout juste posé (cf. TU "cas 9d") ;
  // - puis SIRET, dont dépend verifierSiret() déclenché par TYPE_ENTITE ;
  // - puis IDENTIFIANT_ADRESSAGE et BON_COMMANDE.
  private static readonly ORDRE_TRAITEMENT_FACTURATION: string[] = [
    PreferenceFacturationEnum.CLASSE_PROFIT,
    PreferenceFacturationEnum.SIRET,
    PreferenceFacturationEnum.TYPE_ENTITE,
    PreferenceFacturationEnum.IDENTIFIANT_ADRESSAGE,
    PreferenceFacturationEnum.NO_EXECUTANT,
    PreferenceFacturationEnum.BON_COMMANDE
  ];

  private LOG: LogService = new LogService(TypeFacturationComponent);

  @Input() uid = '';
  public currentUid = 'TypeFacturation';
  @Input() parentForm: FormGroup;
  @Input() facturation: TypeFacturation;
  @Input() disabled: boolean;
  @Input() indexPage: any;

  @ViewChild(TypeAdresseTEMPOSTComponent) typeAdresseTempostComponent: TypeAdresseTEMPOSTComponent;

  public facturationMessage = new FacturationMessage();

  public step9 = new Step9();

  private siretEtat = '';
  private unsubscribe$ = new Subject<void>();
  private applySiretState: (value: string) => void;

  ngOnDestroy(): void {
    this.LOG.debug('[MERGE-FACT][ON-DESTROY]');
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public ngOnInit() {
    this.LOG.debug('[MERGE-FACT][ON-INIT]');
    this.facturationService.getSiret('etat')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (data) => {
          this.siretEtat = data?.valeur || '';
          // Applique la logique sur la valeur initiale une fois le SIRET chargé (cas édition)
          const classeProfitPath = '_SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region';
          const initialValue = this.parentForm?.get(classeProfitPath)?.value;
          if (initialValue && this.applySiretState) {
            this.applySiretState(initialValue);
          }
        }
      });
  }

  public ngAfterViewInit() {
    const classeProfitPath = '_SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region';
    const classeProfitControl = this.parentForm.get(classeProfitPath);

    this.applySiretState = (value: string) => {
      const siretControl = this.parentForm.get(this.getUid() + '_SepSep_TypeAdresseTEMPOST_SepAtt_siret');
      if (value === 'etat' && this.siretEtat) {
        siretControl?.setValue(this.siretEtat);
        siretControl?.disable();
      } else if (value !== 'etat') {
        if (siretControl?.value === this.siretEtat) {
          siretControl?.setValue('');
        }
        siretControl?.enable();
      }
    };

    classeProfitControl?.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(value => this.applySiretState(value));
  }


  private rangTraitementFacturation(code: string): number {
    const idx = TypeFacturationComponent.ORDRE_TRAITEMENT_FACTURATION.indexOf(code);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  }

  getUid() {
    return this.uid + '_SepSep_' + this.currentUid;
  }

  public onTypeEntiteChange(event: TypeEntiteChangeEvent): void {
    if (!this.facturation) {
      return;
    }
    this.facturation.typeEntite = event.typeEntite;

    // Vider le FormControl (fait par TypeAdresseTEMPOSTComponent) ne suffit pas : Fact() réinjecte
    // facturation.<champ> dans le payload dès que le contrôle vaut null, ce que produit reset().
    // `null` et pas `undefined`, que le binding [value] afficherait tel quel dans le champ.
    if (event.hasChanged && event.typeEntite) {
      this.facturation.numBonCommande = null;
      this.facturation.identifiantAdressage = null;
      this.facturation.noServiceExecutant = null;
    }
  }

  chargeDonneePreference(preference: Preference) {
    const values = [...preference.content.values]
      .sort((a, b) => this.rangTraitementFacturation(a.code) - this.rangTraitementFacturation(b.code));

    values.forEach(el => {
      switch (el.code) {
        case PreferenceFacturationEnum.NO_EXECUTANT:
        case PreferenceFacturationEnum.BON_COMMANDE:
        case PreferenceFacturationEnum.TYPE_ENTITE:
          this.parentForm.get(this.getUid() + '_SepAtt_' + el.code)?.setValue(el.value);
          this.parentForm.get(this.getUid() + '_SepAtt_' + el.code)?.markAsTouched();
          // Répercuter sur facturation (lié aux @Input de TypeAdresseTEMPOSTComponent) : sans ça,
          // les *ngIf d'affichage du composant enfant (basés sur typeEntite) ne se remettent jamais
          // à jour, seul le FormControl changerait.
          if (this.facturation) {
            (this.facturation as any)[el.code] = el.value;

            if (el.code === PreferenceFacturationEnum.TYPE_ENTITE) {
              // REQ3936109 : la valeur de préférence est transmise pour que le composant puisse
              // détecter une éventuelle divergence avec la réponse G2B (qui prévaut toujours).
              this.typeAdresseTempostComponent?.verifierSiret(true, el.value as TypeEntite);
            }
          }
          break;

        case PreferenceFacturationEnum.IDENTIFIANT_ADRESSAGE: {
          this.parentForm.get(this.getUid() + '_SepAtt_' + el.code)?.setValue(el.value);
          this.parentForm.get(this.getUid() + '_SepAtt_' + el.code)?.markAsTouched();
          if (this.facturation) {
            this.facturation.identifiantAdressage = el.value;
          }
          break;
        }

        case PreferenceFacturationEnum.CLASSE_PROFIT:
          this.parentForm.get('_SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region')?.setValue(el.value);
          this.parentForm.get('_SepSep_TypeFacturation_SepSep_TypeAdresseTEMPOST_SepSep_ClasseProfit_SepAtt_region')?.markAsTouched();
          break;

        default:
          this.parentForm.get(this.getUid() + '_SepSep_TypeAdresseTEMPOST_SepAtt_' + el.code)?.setValue(el.value);
          this.parentForm.get(this.getUid() + '_SepSep_TypeAdresseTEMPOST_SepAtt_' + el.code)?.markAsTouched();
          break;
      }
    });

    const identifiantAdressageEl = values.find(v => v.code === PreferenceFacturationEnum.IDENTIFIANT_ADRESSAGE);
    const typeEntiteEl = values.find(v => v.code === PreferenceFacturationEnum.TYPE_ENTITE);
    if (identifiantAdressageEl && !typeEntiteEl) {
      const siretValue = this.parentForm.get(this.getUid() + '_SepSep_TypeAdresseTEMPOST_SepAtt_siret')?.value;
      if (siretValue) {
        // "fromPreference : true" active le contrôle sur identifiant absent de la liste G2B
        this.typeAdresseTempostComponent?.loadIdentifiantsAdressage(siretValue, true);
      }
    }
  }
}
